import { action, observable } from 'mobx';
import { DynamicMapStoreBase } from './DynamicMapStoreBase';
import { DynamicProfileStoreBase } from './DynamicProfileStoreBase';
import { DynamicTableStoreBase } from './DynamicTableStoreBase';
import { Disposer, IDisposable } from '../../../framework/Disposer';
import { DynamicProfileLayerType, ProfileKeyCode, ProfileStoreType, SketchState } from './DynamicProfileEnums';
import { INewVertexInfo } from './DynamicProfileInterfaces';

/**
 * The hub to assist the comunications among the actors: map, profile, table, ...
 */
abstract class DynamicProfileHubBase implements IDisposable {
  protected readonly disposer: Disposer = new Disposer();

  protected _profileStore: DynamicProfileStoreBase | undefined;
  protected _profileStore1: DynamicProfileStoreBase | undefined;
  protected _profileStore2: DynamicProfileStoreBase | undefined;
  protected _profileStore3: DynamicProfileStoreBase | undefined;
  protected _mapStore: DynamicMapStoreBase | undefined;
  protected _tableStore: DynamicTableStoreBase | undefined;

  public _isTerraceAlignment: boolean = false;

  /** broadcast station */
  @observable MapSketchState = { state: SketchState.INIT, keyCode: ProfileKeyCode.DEFAULT };

  @observable NewMapVertexInfo: INewVertexInfo | null = null;
  @observable InsertMapVertexInfo: any = null;
  @observable MoveMapVertexInfo: any = null;
  @observable MoveProfileVertexInfo: any = null;
  @observable InsertProfileVertexInfo: any = null;

  @observable TableBorneVertexIndexToDeleteInfo: any = null;
  @observable ProfileVertexIndexToDeleteInfo: any = null;

  @observable NewProfileVertexInfo: INewVertexInfo | null = null;
  @observable ProfileSketchState = { state: SketchState.INIT, keyCode: ProfileKeyCode.DEFAULT };

  // @observable guardrailInfo: any = null;

  constructor(isTerraceAlignment: boolean = false) {
    this._isTerraceAlignment = isTerraceAlignment;
  }

  /** Use a factory method pattern: create an actor only when it's needed; make it possible to
   *  expand with complexity through inheritance hierarchy of stores if needed */
  public abstract createStore(storeType: ProfileStoreType);

  get ProfileStore() {
    return this._profileStore;
  }

  get ProfileStore1() {
    return this._profileStore1;
  }

  get ProfileStore2() {
    return this._profileStore2;
  }

  get ProfileStore3() {
    return this._profileStore3;
  }

  get MapStore() {
    return this._mapStore;
  }

  get TableStore() {
    return this._tableStore;
  }

  /** "Next" observables */
  @action
  async NextNewVertexInfo(info) {
    this.NewMapVertexInfo = info;
  }

  @action
  async NextInsertVertexInfo(info) {
    this.InsertMapVertexInfo = info;
  }
  @action
  async NextInsertProfileVertexInfo(info) {
    this.InsertProfileVertexInfo = info;
  }

  @action
  async NextMoveVertexInfo(info) {
    this.MoveMapVertexInfo = info;
  }

  @action
  async NextMoveProfileVertexInfo(info) {
    this.MoveProfileVertexInfo = info;
  }

  @action
  NextMapSketchState(state) {
    this.MapSketchState = state;
  }

  @action
  NextTableBorneVertexIndexToDelete(info: any) {
    this.TableBorneVertexIndexToDeleteInfo = info;
  }

  @action
  NextProflieSketchState(state) {
    this.ProfileSketchState = state;
  }

  @action
  async NextNewProfileVertexInfo(info) {
    this.NewProfileVertexInfo = info;
  }

  // @action
  // NextGuardrailInfo(info) {
  //   this.guardrailInfo = info;
  // }

  /** delegations */
  SketchPolyline() {
    this.MapStore?.SketchPolyline();
  }

  toggleAlignmentSnapping() {
    this.MapStore?.toggleAlignmentSnapping();
  }

  InsertVertex(applyDefaultCurves?) {
    this.MapStore?.SketchPoint(applyDefaultCurves);
  }

  MoveVertex(applyDefaultCurves?) {
    this.MapStore?.ReshapePolyline(applyDefaultCurves);
  }

  completeMapSketch() {
    this.MapStore?.completeSketch();
  }

  async checkToConvertAlignment(index, fromDeletion) {
    await this.MapStore?.checkToConvertAlignment(index, fromDeletion);
  }

  async redrawAlignmentProfile() {
    await this.MapStore?.redrawAlignmentProfile();
  }

  startLengthyProfileProcess(state: boolean) {
    this.ProfileStore?.raiseStartLengthyProcessEvent(state)
  }

  updateAlignmentOnMap(data: any, index) {
    this.MapStore?.updateAlignmentOnMap(data, index);
  }

  /** init data */
  LoadEditToMap(data: any, keyCode: ProfileKeyCode) {
    this.MapStore?.LoadEditToMap(data, keyCode);
  }

  DrawCurve(data: any) {
    this.MapStore?.drawCurve(data);
  }

  LoadProfile(data: any, keyCode: ProfileKeyCode) {
    this.ProfileStore?.LoadProfile(data, keyCode);
  }

  drawCurvedProfile(data: any, keyCode: ProfileKeyCode) {
    this.ProfileStore?.drawCurvedProfile(data, keyCode);
  }

  /** clean up */
  cleanUpMap() {
    this.MapStore?.cleanUp();
  }

  removeMapLayer(layer: DynamicProfileLayerType) {
    this.MapStore?.removeMapLayer(layer);
  }

  /** dispose */
  Dispose() {
    this.disposer.Dispose();
  }
}

export { DynamicProfileHubBase };
